import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Target } from 'lucide-react';
import { ReactComponent as AbsIcon } from '../../../assets/icons/abs.svg';
import { ReactComponent as BackIcon } from '../../../assets/icons/back.svg';
import { ReactComponent as ChestIcon } from '../../../assets/icons/chest.svg';
import { ReactComponent as ArmIcon } from '../../../assets/icons/arm.svg';
import { ReactComponent as LegIcon } from '../../../assets/icons/leg.svg';
import { ReactComponent as ShoulderIcon } from '../../../assets/icons/shoulder.svg';
import { ReactComponent as GlutesIcon } from '../../../assets/icons/glutes.svg';
import { ReactComponent as ScaleIcon } from '../../../assets/icons/scale.svg';
import { ReactComponent as BodyWeight } from '../../../assets/icons/bodyweight.svg';
import { ReactComponent as WeightliftingIcon } from '../../../assets/icons/weightlifting.svg';

const StepTwo = ({ formData, updateFields }) => {
  const objectives = [
    { label: 'Muscle Gain', icon: <ArmIcon className="w-9 h-8 text-white" /> },
    { label: 'Weight Loss', icon: <ScaleIcon className="w-9 h-8 text-white" /> },
    { label: 'Strength/Endurance', icon: <WeightliftingIcon className="w-9 h-8 text-white" /> },
    { label: 'Overall Fitness', icon: <BodyWeight className="w-9 h-8 text-white" /> }
  ];

  // Set initial objective if not already set
  useEffect(() => {
    if (!formData.objective) {
      updateFields({ objective: objectives[0].label });
    }
  }, []);

  const [currentIndex, setCurrentIndex] = useState(
    formData.objective ? objectives.findIndex(obj => obj.label === formData.objective) : 0
  );
  const [isAnimating, setIsAnimating] = useState(false);

  const handleNext = () => {
    if (isAnimating || currentIndex === objectives.length - 1) return;
    setIsAnimating(true);
    setCurrentIndex(currentIndex + 1);
    updateFields({ objective: objectives[currentIndex + 1].label });
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handlePrev = () => {
    if (isAnimating || currentIndex === 0) return;
    setIsAnimating(true);
    setCurrentIndex(currentIndex - 1);
    updateFields({ objective: objectives[currentIndex - 1].label });
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handleCardClick = (index) => {
    if (isAnimating || index === currentIndex) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    updateFields({ objective: objectives[index].label });
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handleFocusAreaChange = (area) => {
    let newFocusAreas;
    if (area === 'No preference') {
      newFocusAreas = ['No preference'];
    } else {
      const currentAreas = formData.focusAreas || [];
      if (currentAreas.includes('No preference')) {
        newFocusAreas = [area];
      } else {
        newFocusAreas = currentAreas.includes(area)
          ? currentAreas.filter(a => a !== area)
          : [...currentAreas, area];
      }
    }
    updateFields({ focusAreas: newFocusAreas });
  };

  const focusAreas = [
    { label: 'No preference', icon: <Target size={24} className="text-gray-600" /> },
    { label: 'Arms', icon: <ArmIcon className="sm:w-11 sm:h-11 w-6 h-6" /> },
    { label: 'Chest', icon: <ChestIcon className="sm:w-11 sm:h-11 w-6 h-6" /> },
    { label: 'Back', icon: <BackIcon className="sm:w-11 sm:h-11 w-6 h-6" /> },
    { label: 'Shoulders', icon: <ShoulderIcon className="sm:w-11 sm:h-11 w-6 h-6" /> },
    { label: 'Legs', icon: <LegIcon className="sm:w-11 sm:h-11 w-6 h-6" /> },
    { label: 'Glutes', icon: <GlutesIcon className="sm:w-11 sm:h-11 w-6 h-6" /> },
    { label: 'Core/Abs', icon: <AbsIcon className="sm:w-11 sm:h-11 w-6 h-6" /> }
  ];

  return (
    <div className="space-y-6 w-full">
      {/* Objectives Section with Carousel */}
      <div className="space-y-2">
        <label className="block text-lg aw-label">Define your main objective</label>
        <div className="relative h-40 rounded-2xl px-2"
          style={{
            background: 'linear-gradient(180deg, #f7f7f9 0%, #ececef 100%)',
            border: '1px solid #d8d8dc',
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06), 0 1px 0 rgba(255,255,255,0.7)'
          }}
        >
          {/* Edge fades */}
          <div className="absolute left-0 top-0 h-full w-20 rounded-l-2xl pointer-events-none z-10"
            style={{ background: 'linear-gradient(to right, #f7f7f9, rgba(247,247,249,0))' }} />
          <div className="absolute right-0 top-0 h-full w-20 rounded-r-2xl pointer-events-none z-10"
            style={{ background: 'linear-gradient(to left, #ececef, rgba(236,236,239,0))' }} />

          {/* Navigation Buttons */}
          <button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className={`aw-round-btn absolute left-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9
              ${currentIndex === 0 ? 'aw-round-disabled' : 'aw-round-red'}`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <button
            onClick={handleNext}
            disabled={currentIndex === objectives.length - 1}
            className={`aw-round-btn absolute right-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9
              ${currentIndex === objectives.length - 1 ? 'aw-round-disabled' : 'aw-round-red'}`}
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Carousel */}
          <div className="relative w-full h-full overflow-hidden px-14">
            <div className="w-full h-full flex items-center justify-center">
              {objectives.map((objective, index) => {
                const position = index - currentIndex;
                const selected = position === 0;
                return (
                  <div
                    key={objective.label}
                    onClick={() => handleCardClick(index)}
                    className={`absolute w-40 transition-all duration-300 ease-in-out
                      ${Math.abs(position) <= 1 ? 'cursor-pointer' : ''}`}
                    style={{
                      transform: `translateX(${position * 95}%) scale(${selected ? 1 : 0.8})`,
                      opacity: Math.abs(position) > 1 ? 0 : 1 - Math.abs(position) * 0.5,
                      zIndex: 10 - Math.abs(position),
                      pointerEvents: Math.abs(position) <= 1 ? 'auto' : 'none'
                    }}
                  >
                    <div className={`aw-card-option ${selected ? 'aw-card-option-selected' : 'aw-card-option-dark'}`}>
                      <div className="flex flex-col items-center">
                        {objective.icon}
                        <span className="text-center font-semibold mt-2">{objective.label}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Focus Areas Section */}
      <div className="space-y-4">
        <label className="block text-lg aw-label">Focus Area</label>
        <div className="grid sm:grid-cols-4 grid-cols-3 gap-3">
          {focusAreas.map(({ label, icon }) => {
            const selected = formData.focusAreas?.includes(label);
            const dimmed = label === 'No preference' && formData.focusAreas?.length > 0 &&
              !formData.focusAreas?.includes('No preference');
            return (
              <button
                type="button"
                key={label}
                onClick={() => handleFocusAreaChange(label)}
                className={`aw-card-option flex flex-col items-center aspect-square justify-center
                  ${selected ? 'aw-card-option-selected' : ''}
                  ${dimmed ? 'opacity-50' : ''}`}
              >
                <div className="mb-1">{icon}</div>
                <span className="text-center text-xs font-semibold">{label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StepTwo;